// src/contexts/NotificationsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  subscribeToNotifications,
  getUnreadNotificationsCount
} from '../services/notificationsService';
import { useAuth } from '../hooks/useAuth';

const NotificationsContext = createContext();

export const NotificationsProvider = ({ children, options = {} }) => {
  const { 
    lazyLoad = true,  // Cargar notificaciones después de que la UI principal esté lista
    timeout = 8000     // Timeout para evitar bloqueos (aumentado a 8 segundos)
  } = options;
  
  const { user, isAuthenticated, isEmergencyMode } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Cargar notificaciones iniciales con manejo seguro de errores
  const loadNotifications = useCallback(async (options = {}) => {
    if (!isAuthenticated || !user || !user.id) return;
    
    // En modo de emergencia, usar datos locales si existen
    if (isEmergencyMode && isEmergencyMode()) {
      try {
        const cachedNotifications = JSON.parse(localStorage.getItem('avlink_cached_notifications') || '[]');
        setNotifications(cachedNotifications);
        setUnreadCount(cachedNotifications.filter(n => !n.is_read).length);
        setInitialized(true);
        return;
      } catch (err) {
        console.warn('Error cargando notificaciones en caché:', err);
        // Continuar con el flujo normal si hay error
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Crear una promesa con timeout para evitar bloqueos
      const notificationsPromise = getUserNotifications(options);
      
      // Promesa de timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout loading notifications')), timeout);
      });
      
      // Carrera entre las promesas
      const { notifications: data, count } = await Promise.race([
        notificationsPromise,
        timeoutPromise
      ]);
      
      if (data) {
        setNotifications(data);
        // Guardar en local para modo offline
        localStorage.setItem('avlink_cached_notifications', JSON.stringify(data));
      } else {
        setNotifications([]);
      }
      
      // Actualizar contador de no leídas
      try {
        const unreadCountData = await getUnreadNotificationsCount();
        setUnreadCount(unreadCountData);
      } catch (countErr) {
        console.error('Error obteniendo contador de notificaciones:', countErr);
        // Calcular manualmente basado en los datos obtenidos
        if (data) {
          setUnreadCount(data.filter(n => !n.is_read).length);
        } else {
          setUnreadCount(0);
        }
      }
      
      setInitialized(true);
    } catch (err) {
      console.error('Error cargando notificaciones:', err);
      setError(err.message);
      // En caso de error, seguimos con un array vacío pero marcamos como inicializado
      setNotifications([]);
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, timeout, user, isEmergencyMode]);

  // Marcar una notificación como leída
  const markAsRead = useCallback(async (notificationId) => {
    try {
      // En modo de emergencia, solo actualizar estado local
      if (isEmergencyMode && isEmergencyMode()) {
        setNotifications(prev => {
          const updated = prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true, read_at: new Date().toISOString() } 
              : notification
          );
          localStorage.setItem('avlink_cached_notifications', JSON.stringify(updated));
          return updated;
        });
        
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }
      
      // Método normal con conexión
      await markNotificationAsRead(notificationId);
      
      // Actualizar estado local
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true, read_at: new Date().toISOString() } 
            : notification
        )
      );
      
      // Actualizar contador
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      return true;
    } catch (err) {
      console.error('Error marcando notificación como leída:', err);
      toast.error('No se pudo marcar la notificación como leída');
      return false;
    }
  }, [isEmergencyMode]);

  // Marcar todas las notificaciones como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      // En modo de emergencia, solo actualizar estado local
      if (isEmergencyMode && isEmergencyMode()) {
        setNotifications(prev => {
          const updated = prev.map(notification => ({ 
            ...notification, 
            is_read: true, 
            read_at: new Date().toISOString() 
          }));
          localStorage.setItem('avlink_cached_notifications', JSON.stringify(updated));
          return updated;
        });
        
        setUnreadCount(0);
        toast.success('Todas las notificaciones marcadas como leídas');
        return true;
      }
      
      // Método normal con conexión
      await markAllNotificationsAsRead();
      
      // Actualizar estado local
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          is_read: true, 
          read_at: new Date().toISOString() 
        }))
      );
      
      // Resetear contador
      setUnreadCount(0);
      
      toast.success('Todas las notificaciones marcadas como leídas');
      return true;
    } catch (err) {
      console.error('Error marcando todas las notificaciones como leídas:', err);
      toast.error('No se pudieron marcar todas las notificaciones como leídas');
      return false;
    }
  }, [isEmergencyMode]);

  // Eliminar una notificación
  const removeNotification = useCallback(async (notificationId) => {
    try {
      // Verificar si la notificación no estaba leída para actualizar contador
      const notification = notifications.find(n => n.id === notificationId);
      const shouldUpdateCounter = notification && !notification.is_read;
      
      // En modo de emergencia, solo actualizar estado local
      if (isEmergencyMode && isEmergencyMode()) {
        setNotifications(prev => {
          const updated = prev.filter(n => n.id !== notificationId);
          localStorage.setItem('avlink_cached_notifications', JSON.stringify(updated));
          return updated;
        });
        
        if (shouldUpdateCounter) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        
        return true;
      }
      
      // Método normal con conexión
      await deleteNotification(notificationId);
      
      // Actualizar estado local
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (shouldUpdateCounter) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      return true;
    } catch (err) {
      console.error('Error eliminando notificación:', err);
      toast.error('No se pudo eliminar la notificación');
      return false;
    }
  }, [notifications, isEmergencyMode]);

  // Manejar recepción de nueva notificación
  const handleNewNotification = useCallback((notification) => {
    setNotifications(prev => {
      const updated = [notification, ...prev];
      // Guardar en local para modo offline
      localStorage.setItem('avlink_cached_notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => prev + 1);
    
    // Mostrar toast con la notificación nueva
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-dark-800 border border-dark-700 shadow-lg rounded-lg pointer-events-auto flex items-center py-3 px-4`}>
        <div className="mr-3 flex-none text-primary-500 text-xl">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M5.85 3.5a.75.75 0 00-1.117-1 9.719 9.719 0 00-2.348 4.876.75.75 0 001.479.248A8.219 8.219 0 015.85 3.5zM19.267 2.5a.75.75 0 10-1.118 1 8.22 8.22 0 011.987 4.124.75.75 0 001.48-.248A9.72 9.72 0 0019.266 2.5z" />
            <path fillRule="evenodd" d="M12 2.25A6.75 6.75 0 005.25 9v.75a8.217 8.217 0 01-2.119 5.52.75.75 0 00.298 1.206c1.544.57 3.16.99 4.831 1.243a3.75 3.75 0 107.48 0 24.583 24.583 0 004.83-1.244.75.75 0 00.298-1.205 8.217 8.217 0 01-2.118-5.52V9A6.75 6.75 0 0012 2.25zM9.75 18c0-.034 0-.067.002-.1a25.05 25.05 0 004.496 0l.002.1a2.25 2.25 0 11-4.5 0z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-grow">
          <h4 className="font-semibold text-white">{notification.title}</h4>
          <p className="text-sm text-white/70">{notification.message}</p>
        </div>
        <div className="ml-4 flex-shrink-0 flex">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-white/50 hover:text-white focus:outline-none"
          >
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    ), { duration: 5000 });
  }, []);

  // Suscribirse a notificaciones en tiempo real cuando el usuario está autenticado
  useEffect(() => {
    // No intentar suscribirse si no hay usuario autenticado o si estamos en modo emergencia
    if (!isAuthenticated || !user || !user.id || (isEmergencyMode && isEmergencyMode())) {
      return;
    }
    
    let subscription = null;
    
    const setupSubscription = async () => {
      try {
        const result = await subscribeToNotifications(handleNewNotification);
        subscription = result?.subscription;
      } catch (error) {
        console.error('Error estableciendo suscripción de notificaciones:', error);
      }
    };
    
    setupSubscription();
    
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        try {
          subscription.unsubscribe();
        } catch (err) {
          console.error('Error al desuscribirse:', err);
        }
      }
    };
  }, [isAuthenticated, user, handleNewNotification, isEmergencyMode]);

  // Cargar notificaciones iniciales cuando el usuario está autenticado
  useEffect(() => {
    // Si no hay usuario autenticado o no tiene ID, no cargar notificaciones
    if (!isAuthenticated || !user || !user.id) {
      return;
    }
    
    if (!initialized) {
      if (lazyLoad) {
        // Si lazyLoad está activado, retrasamos la carga para priorizar la UI
        const timer = setTimeout(() => {
          loadNotifications({ limit: 10 });
        }, 800); // Retraso para permitir que la UI principal se cargue primero
        
        return () => clearTimeout(timer);
      } else {
        // Carga inmediata si lazyLoad está desactivado
        loadNotifications({ limit: 10 });
      }
    } else if (!isAuthenticated) {
      // Resetear estado cuando el usuario no está autenticado
      setNotifications([]);
      setUnreadCount(0);
      setInitialized(false);
    }
  }, [isAuthenticated, loadNotifications, initialized, lazyLoad, user]);

  // Añadir un timeout de seguridad para asegurar que el contador se carga
  useEffect(() => {
    if (!isAuthenticated || !user || !user.id || initialized) {
      return;
    }
    
    const safetyTimer = setTimeout(async () => {
      console.warn('Timeout de seguridad para notificaciones activado.');
      
      try {
        // Intentar al menos cargar notificaciones desde caché
        if (isEmergencyMode && isEmergencyMode()) {
          try {
            const cachedNotifications = JSON.parse(localStorage.getItem('avlink_cached_notifications') || '[]');
            setNotifications(cachedNotifications);
            setUnreadCount(cachedNotifications.filter(n => !n.is_read).length);
          } catch (err) {
            console.error('Error cargando caché de notificaciones:', err);
          }
        } else {
          // Intentar al menos cargar el contador de no leídas
          try {
            const count = await getUnreadNotificationsCount();
            setUnreadCount(count);
          } catch (err) {
            console.error('Error obteniendo contador:', err);
          }
        }
      } finally {
        setInitialized(true);
        setLoading(false);
      }
    }, timeout);
    
    return () => clearTimeout(safetyTimer);
  }, [isAuthenticated, initialized, timeout, user, isEmergencyMode]);

  // Actualizar contador de no leídas periódicamente
  useEffect(() => {
    // No actualizar periódicamente en modo emergencia o sin usuario
    if (!isAuthenticated || !user || !user.id || (isEmergencyMode && isEmergencyMode())) {
      return;
    }
    
    const intervalId = setInterval(async () => {
      try {
        const count = await getUnreadNotificationsCount();
        setUnreadCount(count);
      } catch (err) {
        console.error('Error actualizando contador de notificaciones:', err);
      }
    }, 60000); // Actualizar cada minuto
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, user, isEmergencyMode]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    initialized,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};